import { CommandSchema, type SimState } from './domain/model';
import { command } from './api';
interface ToolContext {
  registerTool(
    tool: {
      name: string;
      title: string;
      description: string;
      inputSchema: object;
      annotations: { readOnlyHint: boolean };
      execute: (input: unknown) => unknown;
    },
    options: { signal: AbortSignal },
  ): void | Promise<void>;
}
export function registerSimulationTools(
  getState: () => SimState | null,
  onState: (s: SimState) => void,
) {
  const context = (document as Document & { modelContext?: ToolContext }).modelContext;
  if (!context?.registerTool) return () => {};
  const lifecycle = new AbortController();
  const tools = [
    {
      name: 'read_dispatch_overview',
      title: 'Leitstellenlage lesen',
      description: 'Liest offene Übungseinsätze und Fahrzeuge dieser lokalen Simulation.',
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: true },
      execute() {
        const s = getState();
        if (!s) throw Error('Leitstelle noch nicht verbunden.');
        return {
          time: s.now,
          incidents: s.incidents
            .filter((i) => i.phase !== 'abgeschlossen')
            .map((i) => ({ id: i.id, title: i.title, phase: i.phase, address: i.address })),
          vehicles: s.vehicles.map((v) => ({
            id: v.id,
            name: v.name,
            kind: v.kind,
            status: v.status,
            incidentId: v.incidentId,
          })),
        };
      },
    },
    {
      name: 'dispatch_simulated_vehicles',
      title: 'Simulierte Fahrzeuge alarmieren',
      description:
        'Alarmiert verfügbare Fahrzeuge verbindlich zu einem Übungseinsatz. Ändert den lokalen Spielstand; keine reale Alarmierung.',
      inputSchema: {
        type: 'object',
        properties: {
          incidentId: { type: 'string' },
          vehicleIds: { type: 'array', items: { type: 'string' }, minItems: 1, maxItems: 30 },
        },
        required: ['incidentId', 'vehicleIds'],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: false },
      async execute(input: unknown) {
        if (!input || typeof input !== 'object') throw Error('Ungültige Eingabe.');
        const parsed = CommandSchema.parse({ ...input, type: 'dispatch' });
        const result = await command(parsed);
        onState(result.state);
        return { revision: result.state.revision, dispatched: true };
      },
    },
  ];
  for (const tool of tools) {
    try {
      Promise.resolve(context.registerTool(tool, { signal: lifecycle.signal })).catch(() => {});
    } catch {
      /* Optional API; ordinary browsers remain fully usable. */
    }
  }
  return () => lifecycle.abort();
}
